import type { CSSProperties } from 'react'

/**
 * Bingkai Kenangan — plate geometry.
 *
 * Every screen was delivered as a flat 1920x1080 plate, and `View.tsx` wraps
 * custom paths in a scale-to-fit shell (`useScaleToFitShell`, View.tsx:524), so
 * pages position against this canvas in raw design pixels and the shell handles
 * the rest. Do not introduce a second scaling layer.
 */
export const BK_CANVAS = { width: 1920, height: 1080 } as const

export const BK_ASSET = (name: string): string => `/assets/bingkai-kenangan/${name}`

export interface BkRect {
  left: number
  top: number
  width: number
  height: number
}

/**
 * Interactive art — the swan plaques, the payment frames, the swatch panels — is
 * BAKED INTO the plates, so every control is a transparent overlay sitting on a
 * painted one. Misalignment is invisible in code review and obvious to a guest
 * whose tap does nothing, so these are MEASURED, not estimated.
 *
 * Each rectangle below was read off the 1920x1080 source artwork by segmenting
 * it: the plaques are near-white blobs the cream wallpaper never reaches
 * (min channel >= 240), the ornate frame's opening is a flood fill that refuses
 * to cross gold, and the swatch panel is its deep-red field. Numbers are the
 * resulting bounding boxes, so they are the artwork's own geometry rather than
 * anybody's estimate.
 *
 * If a plate is ever re-exported, re-measure rather than nudge.
 */

/** The ornate frame's opening. Identical on filter, capture, border and sticker. */
export const BK_BIG_FRAME: BkRect = { left: 674, top: 311, width: 956, height: 565 }

/**
 * Where the strip preview goes: the cream panel painted inside the narrow
 * frame's white mat, on its flat interior. The panel runs x 209-501,
 * y 279-851, and its outer ~10px is a shaded edge. This is the part inside
 * that edge. It is the same on the capture, border, border-brenbaru and
 * sticker plates.
 *
 * The strip used to be sized to the whole white mat. An eight-cut strip
 * came out 302x604 over a 292x572 panel, running past the painted edge on
 * every side.
 */
export const BK_STRIP_PANEL: BkRect = { left: 220, top: 292, width: 270, height: 546 }

/**
 * A strip's box inside the panel: as large as fits at the strip's own
 * proportions, centred. It is computed rather than left to CSS, because an
 * `aspect-ratio` box that is `h-full` ignores its width limit and overflows
 * sideways.
 */
export const stripBox = (canvasWidth = 600, canvasHeight = 2400): BkRect => {
  const ratio = canvasWidth / canvasHeight
  const p = BK_STRIP_PANEL
  const width = Math.min(p.width, p.height * ratio)
  const height = width / ratio
  return {
    left: p.left + (p.width - width) / 2,
    top: p.top + (p.height - height) / 2,
    width,
    height
  }
}

/**
 * Where the page timeout shows: bottom-right, on the lace border. Rendered on
 * every plate against the alternatives — top corners land on the ribbon,
 * bottom-centre crowds the payment keys — and this is the one spot clear on all.
 */
export const BK_TIMER: BkRect = { left: 1696, top: 990, width: 150, height: 58 }

/** The painted 5x3 sticker grid. Cell 162x151, gutters 17px — see BK_STICKER_GAP. */
/**
 * The filter page's gallery wall: up to eight of the plate's own hanging
 * plaques (bow, ribbon and wooden frame, lifted off the original plate) in two
 * columns of four, between the lace border and the gold frame. Each ribbon runs
 * up behind the plaque above it, as the two plaques on the original plate did.
 */
export const BK_FILTER_PLAQUE = { width: 230, height: 217 } as const
/** The cream label inside one plaque, relative to the plaque. */
export const BK_FILTER_LABEL: BkRect = { left: 40, top: 109, width: 154, height: 85 }
export const BK_FILTER_WALL: readonly { left: number; top: number }[] = [
  { left: 88, top: 205 },
  { left: 338, top: 205 },
  { left: 88, top: 368 },
  { left: 338, top: 368 },
  { left: 88, top: 531 },
  { left: 338, top: 531 },
  { left: 88, top: 694 },
  { left: 338, top: 694 }
]

export const BK_STICKER_GRID: BkRect = { left: 708, top: 350, width: 881, height: 486 }
export const BK_STICKER_GAP = 17

export const BK_HIT = {
  welcome: {
    /** "start", lettered across the ribbon; the ribbon around it is the target. */
    start: { left: 800, top: 630, width: 320, height: 150 } as BkRect
  },
  payment: {
    /**
     * The two hanging plaques, outer wooden frame included — a guest taps the
     * picture, not its mat. Measured off the plate rather than from the white
     * blob inside each one: the mat's left edge sits in shadow, so segmenting
     * on brightness finds only its lit half and lands the target ~100px right.
     */
    qr: { left: 442, top: 248, width: 495, height: 633 } as BkRect,
    cash: { left: 1039, top: 244, width: 494, height: 629 } as BkRect,
    /**
     * The cream panel INSIDE each plaque, measured off the white mat ring that
     * separates it from the wooden frame. Content goes here, not in the plaque
     * rectangle above — that one includes the frame, so anything centred in it
     * sits on the woodwork.
     */
    qrPanel: { left: 509, top: 319, width: 336, height: 497 } as BkRect,
    /**
     * Where the code and price actually go: below the painted "QR / scan me"
     * lettering, which occupies y 347-439 of the panel. Centring in the whole
     * panel put the code on top of the words.
     */
    qrContent: { left: 509, top: 455, width: 336, height: 361 } as BkRect,
    voucherPanel: { left: 1105, top: 313, width: 336, height: 497 } as BkRect,
    /**
     * The print-count notes: the bare wallpaper left of the QR plaque, between
     * the ribbon's tail (lower edge y ~400 here) and the gold bow in the corner
     * (y ~890), clear of the lace border on one side and the plaque's gold
     * vines (x >= 400) on the other.
     */
    prints: { left: 80, top: 420, width: 320, height: 460 } as BkRect,
    /**
     * The "redeem" swan (image incl. 12px of shadow each side), crossing the
     * cash plaque's bottom edge the way the plate swans cross their frames,
     * clear of the painted RM note above it.
     */
    redeem: { left: 1126, top: 695, width: 323, height: 210 } as BkRect
  },
  filter: {
    start: { left: 1050, top: 879, width: 213, height: 129 } as BkRect
  },
  capture: {
    retake: { left: 1075, top: 882, width: 237, height: 143 } as BkRect,
    confirm: { left: 1321, top: 883, width: 237, height: 143 } as BkRect
  },
  /** Border pages live on `frame`; the sticker page on `edit`. Same swans. */
  frame: {
    confirm: { left: 245, top: 876, width: 237, height: 144 } as BkRect,
    moreDesign: { left: 1321, top: 883, width: 237, height: 143 } as BkRect,
    /**
     * The collab plate was drawn without a "more design" swan, which strands a
     * guest who wanted to go back. The two border plates are pixel-identical
     * outside that swan (checked: 0% difference in the surrounding wallpaper),
     * so the swan is lifted from the house plate and laid over the collab one
     * at its own coordinates — it reads as painted because the ground beneath
     * it is the same ground.
     */
    moreDesignPatch: { left: 1290, top: 855, width: 300, height: 190 } as BkRect
  },
  edit: {
    confirm: { left: 245, top: 876, width: 237, height: 144 } as BkRect
  },
  select: {
    printNow: { left: 1229, top: 724, width: 357, height: 216 } as BkRect,
    /**
     * The strip the guest confirms, over the painted envelope.
     *
     * The plate has no slot for it — the envelope is sealed and the filmstrip is
     * painted BEHIND it — so it sits on the envelope's left half, clear of the
     * "preview" ribbon above and the "print now" swan to the right. Screen 9
     * would need redrawing for anything better.
     */
    strip: { left: 697, top: 180, width: 420, height: 720 } as BkRect
    /**
     * There is deliberately NO strip rectangle here. The preview plate is a
     * sealed envelope with a filmstrip tucked BEHIND it, and an overlay can
     * only ever draw in front — a strip placed in the filmstrip would sit on
     * top of the envelope and read as a mistake, and anywhere else covers the
     * envelope the screen is built around. The guest has already seen and
     * confirmed the live strip on `edit`; this screen is the commit.
     *
     * If a real preview is wanted here, screen 9 needs redrawing with a slot.
     */
  },
  /** The cut picker rides on `checkout` — see Checkout.tsx. */
  checkout: {
    /**
     * Plaque AND its strip illustration, as one target — the illustration is
     * the part a guest actually looks at, so it has to be tappable too.
     */
    two: { left: 440, top: 356, width: 468, height: 470 } as BkRect,
    three: { left: 940, top: 356, width: 580, height: 470 } as BkRect
  },
  result: {
    /** Centred on the doily field (389,250 1166x689), below its "scan me". */
    qr: { left: 800, top: 460, width: 320, height: 320 } as BkRect,
    /**
     * The "done" swan. The plate has no button, so this one is ours: the
     * "more design" swan from the frame plate with its words taken out and
     * "done" set in the plate's own stitch lettering (d, n from "design"; o, e
     * from "more" — no letter was drawn fresh). The image carries 12px of
     * shadow on every side; it straddles the doily's hem the way the other
     * swans straddle the gold frame.
     */
    done: { left: 824, top: 860, width: 273, height: 179 } as BkRect
  }
} as const

/** Absolute positioning for a measured rectangle. */
export const bkHit = (r: BkRect): CSSProperties => ({
  position: 'absolute',
  left: r.left,
  top: r.top,
  width: r.width,
  height: r.height
})
